import React, { useEffect, useState } from "react";
import { http } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card } from "../components/ui/card";
import { Plus, Trash2, KeyRound, Loader2, User as UserIcon, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const UsersPage = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showPwd, setShowPwd] = useState(null);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "agent" });
  const [newPwd, setNewPwd] = useState("");

  const load = () => {
    setLoading(true);
    http.get("/users").then((r) => setUsers(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await http.post("/users", form);
      toast.success("Usuario creado");
      setShowCreate(false);
      setForm({ email: "", password: "", name: "", role: "agent" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al crear");
    } finally { setCreating(false); }
  };

  const del = async (id) => {
    try {
      await http.delete(`/users/${id}`);
      toast.success("Usuario eliminado");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al eliminar");
    }
  };

  const changePwd = async () => {
    if (!newPwd || newPwd.length < 6) { toast.error("Mínimo 6 caracteres"); return; }
    try {
      await http.put(`/users/${showPwd.id}/password`, { new_password: newPwd });
      toast.success("Contraseña actualizada");
      setShowPwd(null); setNewPwd("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  if (user?.role !== "admin") return (
    <div className="max-w-xl mx-auto py-20 text-center">
      <ShieldCheck size={40} className="mx-auto text-slate-300 mb-3" />
      <h1 className="font-serif-display text-3xl font-bold text-slate-900">Acceso restringido</h1>
      <p className="text-slate-600 mt-2">Solo el administrador puede gestionar usuarios.</p>
    </div>
  );

  return (
    <div className="max-w-5xl fade-up">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-widest text-sky-600 mb-2">Administración</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">Usuarios</h1>
          <p className="mt-2 text-slate-600 max-w-2xl">Creá cuentas para tu equipo. Los administradores pueden gestionar cotizaciones y usuarios.</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-slate-900 hover:bg-slate-800 text-white rounded-xl h-11 px-5" data-testid="button-new-user">
          <Plus size={18} className="mr-2" /> Nuevo usuario
        </Button>
      </div>

      <div className="space-y-3">
        {loading ? <p className="text-slate-500">Cargando…</p> : users.map((u) => (
          <Card key={u.id} className="p-4 rounded-2xl border border-slate-200 flex items-center gap-4" data-testid={`user-row-${u.id}`}>
            <div className="w-11 h-11 rounded-full bg-sky-50 flex items-center justify-center shrink-0">
              <UserIcon size={18} className="text-sky-600" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-bold text-slate-900 truncate">{u.name || u.email}</p>
                <span className={`text-[10px] uppercase font-bold tracking-widest px-2 py-0.5 rounded-full ${
                  u.role === "admin" ? "bg-sky-100 text-sky-700" : "bg-slate-100 text-slate-600"
                }`}>{u.role}</span>
                {u.id === user.id && <span className="text-[10px] uppercase font-bold tracking-widest px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Vos</span>}
              </div>
              <p className="text-xs text-slate-500 truncate">{u.email}</p>
            </div>
            <Button size="sm" variant="ghost" onClick={() => setShowPwd(u)} title="Cambiar contraseña" data-testid={`user-pwd-${u.id}`}>
              <KeyRound size={16} />
            </Button>
            {u.id !== user.id && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" variant="ghost" className="text-rose-600" data-testid={`user-delete-${u.id}`}><Trash2 size={16} /></Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>¿Eliminar usuario?</AlertDialogTitle>
                    <AlertDialogDescription>{u.email} perderá acceso al sistema.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction onClick={() => del(u.id)} className="bg-rose-600 hover:bg-rose-700">Eliminar</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </Card>
        ))}
      </div>

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nuevo usuario</DialogTitle>
          </DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <div className="space-y-1"><Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Nombre</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Juan Pérez" className="rounded-lg h-10" data-testid="new-user-name" />
            </div>
            <div className="space-y-1"><Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Email</Label>
              <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="juan@voyager.com" className="rounded-lg h-10" data-testid="new-user-email" />
            </div>
            <div className="space-y-1"><Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Contraseña</Label>
              <Input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Mínimo 6 caracteres" className="rounded-lg h-10" data-testid="new-user-password" />
            </div>
            <div className="space-y-1"><Label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Rol</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="rounded-lg h-10" data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="agent">Agente (usa el sistema)</SelectItem>
                  <SelectItem value="admin">Administrador (puede gestionar usuarios)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={() => setShowCreate(false)}>Cancelar</Button>
              <Button type="submit" disabled={creating} className="bg-slate-900 hover:bg-slate-800 text-white rounded-xl" data-testid="submit-new-user">
                {creating && <Loader2 className="animate-spin mr-2" size={14} />}Crear
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change password dialog */}
      <Dialog open={!!showPwd} onOpenChange={(v) => !v && (setShowPwd(null), setNewPwd(""))}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Cambiar contraseña · {showPwd?.email}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} placeholder="Nueva contraseña (min 6)" className="rounded-lg h-10" data-testid="change-pwd-input" />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => { setShowPwd(null); setNewPwd(""); }}>Cancelar</Button>
              <Button onClick={changePwd} className="bg-slate-900 text-white rounded-xl" data-testid="change-pwd-submit">Actualizar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
